//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { type ApiClient, backgroundPollHeaders } from './apiClient';
import {
  type ClearDatasetResponse, type CreateGraphSchemaResponse,
  type DatasetGraphResponse, type DatasetInfo, type FrameGraphResponse,
  type FramePreviewResponse, type RawGraphSchema
} from './apiTypes';

/**
 * Datasets: list dataset info (each entry embeds its schema projection as
 * graph_schema) and the create/clear/delete dataset and frame writes.  The
 * four delete/clear writes return the updated dataset list so callers can
 * seed their cache from the response instead of a follow-up GET.
 */
export class DatasetService {
  constructor(private readonly api: ApiClient) {}

  // Get information about the given dataset from the backend.
  // With no dataset_name provided, retrieves info for all datasets — omit the
  // param entirely rather than sending the literal "undefined".
  getDatasetsInfo = (
    dataset_name?: string, background = false
  ): Promise<DatasetInfo[]> => {
    const url =
      dataset_name ?
      `datasetsinfo?dataset_name=${encodeURIComponent(dataset_name)}` :
      'datasetsinfo';
    return this.api.makeRequest<DatasetInfo[]>(
      url, 'get', null, false, null, backgroundPollHeaders(background));
  };

  createDataset = (
    schema: RawGraphSchema, oldGraph?: string
  ): Promise<CreateGraphSchemaResponse> => {
    const payload = oldGraph ?
      { ...schema, old_graph: oldGraph } : schema;
    return this.api.makeRequest<CreateGraphSchemaResponse>(
      'create_graph', 'post', payload);
  };

  // A page of a frame as a graph.  A frame with no topology answers 400.
  //
  // depth, timeout, seeds, edgeLimit, fanout, sampleSeed and direction act on
  // a vertex frame's expansion only
  // -- the other kinds are reads of a page, with nothing to bound. Seeds are
  // repeated parameters rather than a joined list, since a key may contain a comma.
  //
  // `seedFrames` says which frame each seed belongs to, paired with `seeds` by
  // position -- paired rather than spelled into the seed, for the same reason the
  // seeds are repeated at all.  Omitted, they are all rows of frame_name.  Every
  // named frame must be a vertex frame of the same dataset, which the server
  // refuses otherwise: a walk seeded from another dataset's rows would draw two
  // datasets into one picture.
  frameGraph = (
    frame_name: string, length: number, offset: number,
    options: {
      depth?: number; timeout?: number; seeds?: readonly string[];
      seedFrames?: readonly string[]; edgeLimit?: number;
      // Which relationship types to follow.  Empty or absent follows them all.
      edgeFrames?: readonly string[];
      fanout?: number; nodeFanout?: number; maxEdges?: number;
      sampleSeed?: number;
      direction?: 'forward' | 'reverse' | 'undirected';
      // Whether an edge page's endpoints bring their own rows.  Only an edge
      // frame has endpoints to fetch; a vertex expansion reads its own rows
      // whatever this says.
      properties?: 'rows' | 'none';
    } = { },
    // Dropped by the caller, which frees the browser.  The query the server
    // started runs on to its own deadline, there being nothing here that can
    // reach it -- so a caller offering this should say "stop waiting" and not
    // "cancel".
    signal: AbortSignal | null = null
  ): Promise<FrameGraphResponse> => {
    const params = new URLSearchParams({
      frame_name, length: String(length), offset: String(offset)
    });
    if (options.depth != null) params.set('depth', String(options.depth));
    if (options.timeout != null) params.set('timeout', String(options.timeout));
    if (options.edgeLimit != null)
      params.set('edge_budget', String(options.edgeLimit));
    // How much of any one node may be drawn; zero asks for all of it, leaving
    // the edge budget the only bound.
    if (options.fanout != null) params.set('fanout', String(options.fanout));
    // How many of a node's neighbours may be drawn, as against how many of its
    // edges may be read.  A node whose edges mostly run to one neighbour
    // spends a fanout on that one and arrives with a single neighbour.
    if (options.nodeFanout != null)
      params.set('node_fanout', String(options.nodeFanout));
    // The walk's whole edge budget, beside its whole vertex budget.
    if (options.maxEdges != null)
      params.set('max_edges', String(options.maxEdges));
    // Which sample of a crowded neighborhood to draw.  Not `seed`: that is the
    // rows the walk starts from, repeated once each, and the server reading a
    // single value of it would get the first of those keys.
    if (options.sampleSeed != null)
      params.set('sample_seed', String(options.sampleSeed));
    // Which way an edge is followed.  Undirected is the server's default and
    // what a neighborhood means; the other two ask what a row reaches and what
    // reaches it.
    if (options.direction != null) params.set('direction', options.direction);
    if (options.properties != null)
      params.set('properties', options.properties);
    for (const seed of options.seeds ?? []) params.append('seed', seed);
    for (const frame of options.edgeFrames ?? [])
      params.append('edge_frame', frame);
    for (const frame of options.seedFrames ?? [])
      params.append('seed_frame', frame);

    return this.api.makeRequest<FrameGraphResponse>(
      `framegraph?${params.toString()}`, 'get',
      null, false, null, null, false, false, signal);
  };

  // The rows of a frame named by key: the row behind a node somebody pointed at.
  //
  // A handful of keys rather than every key a sample touched: the row behind
  // one node, for a picture drawn without the endpoints' rows (see
  // datasetGraph).  Repeated parameters rather than a joined list, since a key
  // may contain a comma.
  frameRows = (
    frame_name: string, keys: readonly string[]
  ): Promise<FramePreviewResponse> => {
    const params = new URLSearchParams({ frame_name });
    for (const key of keys) params.append('key', key);

    return this.api.makeRequest<FramePreviewResponse>(
      `framerows?${params.toString()}`, 'get');
  };

  // A whole dataset as a graph.  The length is shared out across its edge
  // frames rather than spent on any one of them, so every frame appears.
  //
  // `properties` says whether the endpoints' own rows are wanted, and they are
  // unless a caller says otherwise.  Reading them used to cost a filter naming
  // every key the sample touched, a pass over the vertex frame per batch; the
  // server resolves them through the key index now, so the read is a probe per
  // endpoint.  With the rows there a color rule matches, a search over
  // properties finds something and a tooltip has something to show before
  // anybody clicks.  Leave them out for a picture too large to want them.
  //
  // A signal, because the largest samples take long enough that somebody may decide they
  // asked for too much: aborting drops the answer on this side and lets the server notice
  // that nobody is waiting for it.
  datasetGraph = (
    dataset_name: string, length: number,
    properties: 'rows' | 'none' = 'rows',
    signal: AbortSignal | null = null
  ): Promise<DatasetGraphResponse> => {
    return this.api.makeRequest<DatasetGraphResponse>(
      `datasetgraph?dataset_name=${encodeURIComponent(dataset_name)}` +
      `&length=${length}&properties=${properties}`, 'get',
      null, false, null, null, false, false, signal);
  };

  // Delete all rows in the given dataset in the backend.  Answers with the new
  // dataset list plus the frames the server would not empty (see
  // ClearDatasetResponse).
  clearDataset = (dataset_name: string): Promise<ClearDatasetResponse> => {
    return this.api.makeRequest<ClearDatasetResponse>(
      `clearDataset?dataset_name=${encodeURIComponent(dataset_name)}`,
      'delete');
  };

  // Delete all rows in the given frame in the backend.
  clearFrame = (frame_name: string): Promise<DatasetInfo[]> => {
    return this.api.makeRequest<DatasetInfo[]>(
      `clearFrame?frame_name=${encodeURIComponent(frame_name)}`, 'delete');
  };

  // Delete the given dataset in the backend.
  deleteDataset = (dataset_name: string): Promise<DatasetInfo[]> => {
    return this.api.makeRequest<DatasetInfo[]>(
      `deleteDataset?dataset_name=${encodeURIComponent(dataset_name)}`,
      'delete');
  };

  // Delete the given frame in the backend.
  deleteFrame = (frame_name: string): Promise<DatasetInfo[]> => {
    return this.api.makeRequest<DatasetInfo[]>(
      `deleteFrame?frame_name=${encodeURIComponent(frame_name)}`, 'delete');
  };

  // A frame's leading rows and column types.  The backend caps `length`, so a
  // caller cannot turn this into a paging API -- it reads over Arrow Flight as
  // a bounded positional fetch, which is what keeps it off the server's main
  // execution arena and out of the jobs list.
  previewFrame = (
    frame_name: string, length = 25, offset = 0
  ): Promise<FramePreviewResponse> => {
    return this.api.makeRequest<FramePreviewResponse>(
      `framepreview?frame_name=${encodeURIComponent(frame_name)}` +
      `&length=${length}&offset=${offset}`, 'get');
  };

  createDemoData = (dataset_name: string): Promise<string> => {
    const data = { dataset_name: dataset_name };
    return this.api.makeRequest<string>('create_demo_data', 'post', data);
  };
}
